import * as L from 'leaflet';
import { Injector } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Feature, Point } from 'geojson';
import { ObservationTypes } from 'src/app/apiAndObjects/objects/dictionaries/observationTypes.enum';
import { SearchResultItem } from 'src/app/apiAndObjects/objects/resultItems/searchResultItem';
import { SelectableObservationSummary } from 'src/app/apiAndObjects/objects/selectableObservationSummary';
import { FaMarker, GeoJsonLayer } from 'src/app/bgsLeaflet/public_api';
import { ObservationsService } from 'src/app/services/observations.service';
import { EVResultsService } from '../../ev-sidebar-results/ev-sidebar-results.service';

export class SearchResultsMapLayer extends GeoJsonLayer {
  private readonly resultsService: EVResultsService;
  private readonly observationsService: ObservationsService;

  constructor(
    private readonly results: Array<SelectableObservationSummary>,
    injector: Injector,
    private readonly sanitizer: DomSanitizer,
  ) {
    super('search_results', 'Search Results');
    this.toggleable.set(false);

    this.resultsService = injector.get<EVResultsService>(EVResultsService);
    this.observationsService = injector.get<ObservationsService>(ObservationsService);

    this.setClusteredMarkers(true, null, true, (markers: Array<L.Marker>) => {
      const myFeatures = markers.map((marker: L.Marker) => marker.feature);
      this.showFeatures(myFeatures);
    });

     
    this.setPointToLayerFunction((geoJsonPoint: Feature<Point, any>, latLng: L.LatLng) => {
      return this.pointToLayer(geoJsonPoint, latLng);
    });

    const features = {
      type: 'FeatureCollection',
      features: results.map((obsSummary: SelectableObservationSummary) => {
        const feature = obsSummary.location;
        feature.id = obsSummary.id;
        return feature;
      }),
    } as GeoJSON.FeatureCollection;

    this.setGeoJsonData(features);
    // trigger opening of results panel for all selected items
    const summaries = results.filter((summary) => summary.isSelected);
    this.setDetailsDisplayResults(summaries, true);
  }

   
  private pointToLayer(geoJsonPoint: Feature<Point, any>, latLng: L.LatLng): L.Layer {
    const leafletMarker = this.getMarkerFromSummaryId(String(geoJsonPoint.id)).getMarker(latLng);
    leafletMarker.addEventListener('click', () => this.showFeatures(geoJsonPoint));
    leafletMarker.feature = geoJsonPoint;
    return leafletMarker;
  }

  private getMarkerFromSummaryId(id: string): FaMarker {
    const observationSummary = this.getObservationSummaryFromId(id);
    return this.getMarker(observationSummary);
  }

  private getMarker(observationSummary: SelectableObservationSummary, useSelectedAttribute = true): FaMarker {
    const markerColor = useSelectedAttribute && observationSummary.isSelected ? '#0096d1' : '#262f41';
    const iconColor = useSelectedAttribute && observationSummary.isSelected ? '#fff' : '#fff';

    // default
    const marker = new FaMarker()
      .configure(['fas', 'fa-map-marker'], markerColor)
      .configureIcon(['fas', 'fa-globe'], iconColor);
    switch (observationSummary.type.id) {
      case ObservationTypes.ASH:
        marker.configure(['fas', 'fa-map-marker'], markerColor).configureIcon(['fas', 'fa-mountain'], iconColor);
        break;
      case ObservationTypes.S02:
        marker.configure(['fas', 'fa-map-marker'], markerColor).configureIcon(['fas', 'fa-cloud'], iconColor);
        break;
    }
    return marker;
  }

  private getObservationSummaryFromId(id: string): SelectableObservationSummary {
    return this.results.find((obs: SelectableObservationSummary) => obs.id === id);
  }

   
  private showFeatures(features: Feature<Point, any> | Array<Feature<Point, any>>): void {
    features = Array.isArray(features) ? features : [features];

     
    const summaries = features.map((feature: Feature<Point, any>) => {
      return this.getObservationSummaryFromId(String(feature.id));
    });
    this.setDetailsDisplayResults(summaries);
  }

  private setDetailsDisplayResults(
    observations: Array<SelectableObservationSummary>,
    useSelectedAttribute = false,
  ): void {
    if (observations.length > 0) {
      const resultItems = observations.map((summary: SelectableObservationSummary) => {
        return new SearchResultItem(summary)
          .setIcon(
            this.sanitizer.bypassSecurityTrustHtml(this.getMarker(summary, useSelectedAttribute).getIcon().outerHTML),
          )
          .setObservationService(this.observationsService);
      });
      this.resultsService.setResults(resultItems);
    }
  }
}
